import type { Node } from "@xyflow/react";
import { useState, useCallback, useMemo, useId } from "react";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@trillion/ui/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@trillion/ui/components/ui/popover";
import { Separator } from "@trillion/ui/components/ui/separator";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuShortcut, DropdownMenuTrigger } from "@trillion/ui/components/ui/dropdown-menu";
import {
  Undo2,
  Redo2,
  Square,
  Circle,
  Diamond,
  Hexagon,
  StickyNote,
  Type,
  UserRound,
  Palette,
  AArrowUp,
  Clipboard,
} from "lucide-react";
import { useCanvasEditability } from "../lib/canvas-editability";
import type { BoardNodeData } from "../types/board";
import { createNodeData, type BoardNodeType } from "../lib/node-creation";
import { getFormattingState, STICKY_COLORS, type FormattingChange } from "../lib/node-formatting";
import { formatNode } from "../lib/canvas-commands";
import { getContrastTextColor } from "../lib/color-utils";
import type { CanvasClipboardActions, ClipboardAction } from "../lib/use-canvas-clipboard";
import { CanvasOrganizationMenu, type CanvasOrganizationMenuProps } from "./canvas-organization-menu";
import { canvasShortcutHint, canvasAriaShortcut } from "../lib/canvas-shortcuts";

// ── Color palette ────────────────────────────────────────────
const COLOR_PALETTE = [
  { value: "", label: "Default" },
  { value: "#ef4444", label: "Red" },
  { value: "#f97316", label: "Orange" },
  { value: "#eab308", label: "Yellow" },
  { value: "#22c55e", label: "Green" },
  { value: "#06b6d4", label: "Cyan" },
  { value: "#3b82f6", label: "Blue" },
  { value: "#8b5cf6", label: "Purple" },
  { value: "#ec4899", label: "Pink" },
  { value: "#64748b", label: "Slate" },
  { value: "#1e293b", label: "Dark" },
  { value: "#ffffff", label: "White" },
];

// ── Font size presets ────────────────────────────────────────
const FONT_SIZES = [
  { value: 12, label: "S" },
  { value: 14, label: "M" },
  { value: 18, label: "L" },
  { value: 24, label: "XL" },
  { value: 32, label: "2XL" },
];

// ── Shape definitions ────────────────────────────────────────
const shapeDefinitions: Array<{
  type: BoardNodeType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { type: "rectangle", label: "Rectangle", icon: Square },
  { type: "circle", label: "Circle", icon: Circle },
  { type: "diamond", label: "Diamond", icon: Diamond },
  { type: "hexagon", label: "Hexagon", icon: Hexagon },
  { type: "stickyNote", label: "Sticky Note", icon: StickyNote },
  { type: "text", label: "Text", icon: Type },
  { type: "orgChartCard", label: "Org Chart Card", icon: UserRound },
];

// ── Toolbar Component ────────────────────────────────────────

interface DiagramToolbarProps {
  nodes: Node[];
  disabled?: boolean;
  onAddNode: (data: BoardNodeData) => void;
  clipboard: CanvasClipboardActions;
  organization?: CanvasOrganizationMenuProps;
  onClipboardClose?: () => void;
}

export function DiagramToolbar({ nodes, disabled: explicitlyDisabled, onAddNode, clipboard, onClipboardClose, organization }: DiagramToolbarProps) {
  const { canEditDocument, isDocumentEditable, execute, canUndo, canRedo, undo, redo, disabledReason } = useCanvasEditability();
  const disabled = explicitlyDisabled || !canEditDocument;
  const [defaultColor, setDefaultColor] = useState("");
  const [defaultFontSize, setDefaultFontSize] = useState(14);
  const descriptionId = useId();
  const formatting = useMemo(() => getFormattingState(nodes, {
    bgColor: defaultColor, fontSize: defaultFontSize,
  }), [nodes, defaultColor, defaultFontSize]);
  const { selectedNode, color: activeColor, size: activeFontSize } = formatting;
  const fillDisabled = disabled || !formatting.fill;
  const fontDisabled = disabled || !formatting.fontSize;
  const fillDescription = disabled ? disabledReason
    : formatting.fillReason || (selectedNode ? "Shape fill" : "Default fill for new shapes");
  const fontDescription = disabled ? disabledReason
    : formatting.fontReason || (selectedNode ? "Text size" : "Default text size for new shapes");
  const palette = formatting.fill === "color"
    ? Object.entries(STICKY_COLORS).map(([value, colors]) => ({
      value, label: value[0].toUpperCase() + value.slice(1), bg: colors.bg,
    }))
    : COLOR_PALETTE.map((color) => ({ ...color, bg: color.value }));
  const activeFill = palette.find((color) => color.value === activeColor)?.bg || activeColor;

  const applyFormatting = useCallback((change: FormattingChange) => {
    if (disabled || !isDocumentEditable() || (change.kind === "fill" ? !formatting.fill : !formatting.fontSize)) return;
    if (selectedNode) {
      execute(formatNode(selectedNode.id, change));
    }
    if (change.kind === "fontSize") setDefaultFontSize(change.value);
    else if (formatting.fill === "bgColor") setDefaultColor(change.value);
  }, [disabled, formatting, selectedNode, execute, isDocumentEditable]);

  const creationData = useCallback((type: BoardNodeType) => createNodeData(type, {
    bgColor: defaultColor, fontSize: defaultFontSize,
  }), [defaultColor, defaultFontSize]);

  const handleDragStart = useCallback((event: React.DragEvent, type: BoardNodeType) => {
    if (disabled || !isDocumentEditable()) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.setData("application/reactflow-type", type);
    event.dataTransfer.setData("application/reactflow-data", JSON.stringify(creationData(type)));
    event.dataTransfer.effectAllowed = "move";
  }, [disabled, creationData, isDocumentEditable]);

  // ── Render ──
  return (
    <TooltipProvider delayDuration={300}>
      <div className="board-editing-toolbar" data-canvas-controls role="group" aria-label="Canvas editing">
        {[{ label: "Undo", action: undo, available: canUndo, Icon: Undo2, key: "undo" as const },
          { label: "Redo", action: redo, available: canRedo, Icon: Redo2, key: "redo" as const }].map(({ label, action, available, Icon, key }) => (
          <Tooltip key={label}>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0" aria-label={label} aria-keyshortcuts={canvasAriaShortcut(key)}
                  disabled={disabled || !available} onClick={action}><Icon className="h-4 w-4" /></Button>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[calc(100vw-2rem)] break-words" side="top">{label} ({canvasShortcutHint(key)})</TooltipContent>
          </Tooltip>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-9 gap-1 px-2" aria-label="Clipboard" aria-busy={clipboard.busy}>
              <Clipboard className="h-4 w-4" /><span className="text-xs">Clipboard</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="max-w-[calc(100vw-2rem)]" side="top" align="start" onCloseAutoFocus={event => {
            if (onClipboardClose) { event.preventDefault(); onClipboardClose(); }
          }}>
            {([{ action: "copy", label: "Copy", key: "copy" }, { action: "cut", label: "Cut", key: "cut" },
              { action: "paste", label: "Paste", key: "paste" }, { action: "duplicate", label: "Duplicate", key: "duplicate" }] satisfies
              { action: ClipboardAction; label: string; key: ClipboardAction }[]).map(({ action, label, key }) => (
              <DropdownMenuItem key={action} disabled={!clipboard.available[action]}
                onSelect={() => { void clipboard[action](); }}>
                {label}<DropdownMenuShortcut>{canvasShortcutHint(key)}</DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Separator orientation="vertical" className="mx-1 h-6" />
        {organization && <CanvasOrganizationMenu {...organization} />}
        {/* Shape buttons */}
        {shapeDefinitions.map((shape, index) => (
          <div key={shape.type}>
            {index === 4 && <Separator orientation="vertical" className="mx-1 h-6" />}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0"
                  disabled={disabled}
                  aria-label={shape.label}
                  onClick={() => { if (!disabled && isDocumentEditable()) onAddNode(creationData(shape.type)); }}
                  draggable={!disabled}
                  onDragStart={(e) => handleDragStart(e, shape.type)}
                >
                  <shape.icon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[calc(100vw-2rem)] break-words" side="top">
                <p>{shape.label}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        ))}

        <Separator orientation="vertical" className="mx-1 h-6" />

        <span id={`${descriptionId}-fill`} className="sr-only">{fillDescription}</span>
        <span id={`${descriptionId}-font`} className="sr-only">{fontDescription}</span>
        {/* ── Color picker ── */}
        <Popover>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex" tabIndex={fillDisabled ? 0 : undefined} aria-describedby={`${descriptionId}-fill`}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-9 w-9 p-0" disabled={fillDisabled}
                    aria-label={selectedNode ? "Shape color" : "Default color"} aria-describedby={`${descriptionId}-fill`}>
                    {activeColor ? (
                      <div
                        className="h-5 w-5 rounded-full border-2 border-foreground/20"
                        style={{ backgroundColor: activeFill }}
                      />
                    ) : (
                      <Palette className="h-4 w-4" />
                    )}
                  </Button>
                </PopoverTrigger>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[calc(100vw-2rem)] break-words" side="top"><p>{fillDescription}</p></TooltipContent>
          </Tooltip>
          <PopoverContent className="w-auto max-w-[calc(100vw-2rem)] p-3" side="top" align="center" sideOffset={8}>
            <div className="flex flex-col gap-2">
              <p className="text-xs font-medium text-muted-foreground">
                {selectedNode ? "Shape fill" : "Default fill for new shapes"}
              </p>
              <div className="grid grid-cols-6 gap-1.5">
                {palette.map((c) => (
                  <button
                    key={c.value || "default"}
                    title={c.label}
                    aria-label={`${c.label} fill`}
                    aria-pressed={activeColor === c.value}
                    disabled={fillDisabled}
                    className="group relative flex h-7 w-7 items-center justify-center rounded-md border transition-transform hover:scale-110"
                    style={{
                      backgroundColor: c.bg || "var(--color-card)",
                      borderColor:
                        activeColor === c.value
                          ? "var(--color-primary)"
                          : c.value === "#ffffff"
                            ? "var(--color-border)"
                            : "transparent",
                      borderWidth: activeColor === c.value ? 2 : 1,
                    }}
                    onClick={() => applyFormatting({ kind: "fill", value: c.value })}
                  >
                    {activeColor === c.value && (
                      <div
                        className="h-2 w-2 rounded-full"
                        style={{
                          backgroundColor: getContrastTextColor(c.bg) ?? "var(--color-foreground)",
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* ── Font size picker ── */}
        <Popover>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex" tabIndex={fontDisabled ? 0 : undefined} aria-describedby={`${descriptionId}-font`}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-9 gap-1 px-2" disabled={fontDisabled}
                    aria-label={selectedNode ? "Text size" : "Default text size"} aria-describedby={`${descriptionId}-font`}>
                    <AArrowUp className="h-4 w-4" />
                    <span className="text-xs tabular-nums">{activeFontSize}</span>
                  </Button>
                </PopoverTrigger>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[calc(100vw-2rem)] break-words" side="top"><p>{fontDescription}</p></TooltipContent>
          </Tooltip>
          <PopoverContent className="w-auto max-w-[calc(100vw-2rem)] p-3" side="top" align="center" sideOffset={8}>
            <div className="flex flex-col gap-2">
              <p className="text-xs font-medium text-muted-foreground">
                {selectedNode ? "Text size" : "Default text size for new shapes"}
              </p>
              <div className="flex gap-1">
                {FONT_SIZES.map((fs) => (
                  <Button
                    key={fs.value}
                    variant={activeFontSize === fs.value ? "default" : "outline"}
                    size="sm"
                    className="h-8 w-10 text-xs"
                    aria-label={`Font size ${fs.value}`}
                    aria-pressed={activeFontSize === fs.value}
                    disabled={fontDisabled}
                    onClick={() => applyFormatting({ kind: "fontSize", value: fs.value })}
                  >
                    {fs.label}
                  </Button>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </TooltipProvider>
  );
}
